import { useIsMobile } from "@/hooks/use-mobile";
import {
  actionsKeys,
  cn,
  formatAmount,
  getRequiredFields,
  invoiceParkStatus,
  MONTHS,
  permissionActions,
  toApiSort,
} from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useGetJournalEntryList,
} from "@/services";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { SEARCH_CRITERIA, SEARCH_TYPE } from "./consts";
import { Badge } from "@/components/ui";

const useListing = () => {
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const form = useForm<formValues>({
    defaultValues: {
      [SEARCH_TYPE]: "",
      [SEARCH_CRITERIA]: "",
    },
    resolver: zodResolver(schema),
  });
  const requiredFields = getRequiredFields(schema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const {currentFinanceYear, accessPermission} = useSelector(
    (state: RootState) => state.currentData
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const {
    data: documentList,
    isLoading: isDocLoading,
    refetch: refetchDoc,
    isRefetching: isDocRefetching,
  } = useGetJournalEntryList({
    data: {
      filters: [
        {
          field: "billingYear",
          value: currentFinanceYear,
          operator: "EQUAL",
        },
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        ...(form.watch()[SEARCH_TYPE]
          ? [
              {
                field: form.watch()[SEARCH_TYPE],
                value: [
                  moment(form.watch()[SEARCH_CRITERIA]?.from).format(
                    "YYYY-MM-DDT00:00:00"
                  ),
                  moment(form.watch()[SEARCH_CRITERIA]?.to).format(
                    "YYYY-MM-DDT23:59:00"
                  ),
                ],
                operator: "BETWEEN",
              },
            ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    refetchDoc();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchDoc();
    }
  }, [globalFilter]);

  const onSubmit = () => {
    form.setValue(SEARCH_TYPE, "createdAt");
    setTimeout(() => refetchDoc(), 100);
  };

  const onClear = () => {
    form.reset();
    setTimeout(() => refetchDoc());
  };

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.collection}`,
        name: "Collection",
      },
      {
        link: "",
        name: <span className="text-primary">Unified Journal Entry</span>,
      },
    ],
    []
  );

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}/${routes.collection}`);
  };

  // Define Table Columns
  const columns = [
    {
      accessorKey: "journalEntryId",
      header: <div>Document Header ID</div>,
      accessorFn: (row: any) => String(row?.journalEntryId || ""),
    },
    {
      accessorKey: "billingMonth",
      header: <div>Billing Period</div>,
      accessorFn: (row: any) => MONTHS[row?.billingMonth - 1] || "",
    },
    {
      accessorKey: "createdAt",
      header: <div>Processed Date</div>,
      accessorFn: (row: any) =>
        row?.createdAt ? moment(row?.createdAt || "").format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "amount",
      meta: { align: "right" },
      header: <div>Collected Amount</div>,
      accessorFn: (row: any) => formatAmount(row?.amount || "0"),
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: (row: any) => (
        <Badge
          variant="outline"
          className={cn(
            invoiceParkStatus[row.row.original.status?.toLowerCase()]?.color,
            "rounded-full px-4 py-2"
          )}
        >
          {invoiceParkStatus[row?.row?.original?.status?.toLowerCase()]?.label}
        </Badge>
      ),
    },
  ];

  const hasAddPermission = useMemo(() => accessPermission?.[actionsKeys?.UNIFIED_JE?.toLowerCase()][permissionActions.CREATE], [accessPermission]);

  const onRowClick = (row: any) => {
    navigate(`${row.original.journalEntryId}/view`);
  };

  const onAdd = () => {
    navigate(`${routes.add}`);
  };

  return {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    isMobile,
    onRowClick,
    isDocLoading,
    documentList,
    isDocRefetching,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    form,
    requiredFields,
    onSubmit,
    onClear,
    communityDetail,
    onAdd,
    hasAddPermission,
    onBackClick,
    currentFinanceYear
  };
};

export default useListing;
